import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Pie } from "@ant-design/plots";
import styled from "@emotion/styled";
import { Col, Row, Spin, Select, Form } from "antd";
import { chain, map, size, uniqBy } from "lodash";
import {
  getLocations,
  getUserRoleId,
  getSelectedDepartmentId,
  getClientid,
  getUserList,
  getUserInfo,
} from "redux/selectors";
import { CALL_API } from "common/API";
import LocationBasedAttendanceList from "./LocationBasedAttendanceList";
import { getCountryList } from "redux/selectors";
import { connect, useSelector } from "react-redux";

const StyledChartContainer = styled.div`
  min-height: 504px;
  padding: 20px !important;
  box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px;
  border-radius: 8px !important;
  .name-title {
    padding: 3px 10px;
    font-size: 1rem;
    padding-bottom: 15px;
    font-weight: 600;
  }
  .rowstyle {
    display: flex;
    justify-content: space-between;
  }
  .legends {
    padding-top: 3px;
    display: flex;
    width: 100%;
    justify-content: center;
    flex-direction: column;
    gap: 4px;
    padding: 16px;
    div {
      display: flex;
      justify-content: start;
      align-items: center;
    }
    .legend-box {
      width: 100%;
      display: flex;
      justify-content: start;
      align-items: center;
      gap: 12px;
      font-size: 12px;
      font-weight: 500;
      @media (max-width: 426px) {
        font-size: 8px;
      }
      strong {
        margin-left: auto;
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
`;

const StyledColorBox = styled.span`
  ${({ color }) => color && `background: ${color};`}
  height: 14px;
  width: 28px;
  border-radius: 10px;
`;

// Outside component — never recreated on render
const COLOR_PALETTE = [
  "#4E79A7", "#F28E2B", "#E15759", "#76B7B2",
  "#59A14F", "#EDC948", "#B07AA1", "#FF9DA7",
  "#9C755F", "#BAB0AC",
];

function LocationBasedPresentStaff({
  selectedDepartmentId,
  ClientId,
  myTeam,
  userList,
  isHrDashboard,
  userInfo,
  isMyTeam,
}) {
  const [displayReportLoader, toggleReportLoader] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportList, setReportList] = useState([]);
  const [locationName, setLocationName] = useState("");
  const [displayAttendanceStaffModal, toggleDisplayAttendanceStaffModal] = useState(false);
  const [form] = Form.useForm();

  const countryList = useSelector(getCountryList);

  // Computed once via ref — not recomputed on every render
  const currentUserRef = useRef(null);
  if (!currentUserRef.current) {
    currentUserRef.current = userList?.find(
      (item) => item?.userid === userInfo?.userid
    );
  }
  const currentUser = currentUserRef.current;

  // All primitives — stable across renders
  const employeeId = currentUser?.employeeid ?? "";
  const officeSupervisorId = currentUser?.OfficeSupervisorId ?? "";
  const roleId = currentUser?.roleid ?? "";

  // Stringify array once so it's a stable primitive dep
  const managingOfficeStr = useMemo(
    () => JSON.stringify(currentUser?.managingoffice ?? ""),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // read once from ref — never changes
  );

  const [selectedManagingOffice, setSelectedManagingOffice] = useState(() => {
    const mo = currentUser?.managingoffice;
    return Array.isArray(mo) ? mo[0] : mo ?? "";
  });

  const getReportData = useCallback(async () => {
    if (!ClientId) return;
    toggleReportLoader(true);
    try {
      const managingOfficeParsed = JSON.parse(managingOfficeStr);

      const { locationList } = await CALL_API(
        `location-list/${ClientId}`,
        "post",
        {
          officesupervisorid: myTeam
            ? officeSupervisorId && officeSupervisorId !== "none"
              ? [officeSupervisorId]
              : [employeeId]
            : [""],
          country: myTeam
            ? ""
            : selectedManagingOffice
              ? [selectedManagingOffice]
              : Array.isArray(managingOfficeParsed)
                ? managingOfficeParsed
                : [managingOfficeParsed],
        }
      );

      // Fixed color palette — no random colors that cause chart re-renders
      const result = chain(locationList)
        .groupBy((x) => x.baselocationname)
        .map((value, key, __, index) => ({
          loginLocationName: key,
          prensentList: value,
          presentCount: size(value),
        }))
        .map((item, index) => ({
          ...item,
          color: COLOR_PALETTE[index % COLOR_PALETTE.length],
        }))
        .value();

      setReportData(result);
    } catch (err) {
      console.error("Failed to fetch location data:", err);
    } finally {
      toggleReportLoader(false);
    }
  }, [ClientId, employeeId, officeSupervisorId, managingOfficeStr, selectedManagingOffice, myTeam]);
  // Only fires on mount and when user explicitly changes department or country
  useEffect(() => {
    getReportData();
  }, []);

  // Memoized config — Pie chart won't re-render unless reportData actually changes
  const config = useMemo(() => ({
    padding: "auto",
    data: reportData,
    angleField: "presentCount",
    colorField: "loginLocationName",
    radius: 0.7,
    color: reportData.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]),
    label: {
      type: "inner",
      offset: "-30%",
      autoRotate: false,
      style: { fontSize: 12, textAlign: "center" },
    },
    legend: false,
    interactions: [{ type: "element-active" }],
  }), [reportData]);

  const uniqueCountryList = useMemo(
    () =>
      uniqBy(
        countryList.map((c) => ({ label: c.countryname, value: c.countryname })),
        "value"
      ),
    [countryList]
  );

  // Stable callback — won't cause LocationBasedAttendanceList to re-render
  const handleCloseModal = useCallback(() => {
    toggleDisplayAttendanceStaffModal(false);
  }, []);

  const handlePieClick = useCallback((plot) => {
    plot.on("element:click", (x) => {
      setLocationName(x.data.data.loginLocationName);
      setReportList(x.data.data.prensentList);
      toggleDisplayAttendanceStaffModal(true);
    });
  }, []);

  return (
    <>
      <Spin spinning={displayReportLoader}>
        <StyledChartContainer>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className="name-title">Today's Location based Attendance</div>

            {roleId?.includes("2") && !isMyTeam && (
              <Select
                showSearch
                placeholder="Select Country"
                style={{ width: 200 }}
                value={selectedManagingOffice || undefined}
                onChange={(value) => {
                  form.setFieldsValue({ managingoffice: value });
                  setSelectedManagingOffice(value || "");
                }}
              >
                {uniqueCountryList.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          </div>

          {reportData?.length > 0 ? (
            <Row className="rowstyle">
              <Col span={16}>
                <Pie {...config} onEvent={handlePieClick} />
              </Col>
              <Col span={8} className="legends">
                {reportData.map((val, i) => (
                  <div key={val.loginLocationName}>
                    <div className="legend-box">
                      <StyledColorBox color={COLOR_PALETTE[i % COLOR_PALETTE.length]} />
                      {val.loginLocationName}
                    </div>
                  </div>
                ))}
              </Col>
            </Row>
          ) : (
            <p style={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              No Staff Present
            </p>
          )}
        </StyledChartContainer>
      </Spin>

      <LocationBasedAttendanceList
        displayModal={displayAttendanceStaffModal}
        toggleModal={handleCloseModal}
        title={locationName}
        list={reportList}
        reportType={locationName}
        userList={userList}
        userInfo={userInfo}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  locationList: getLocations(state),
  userRoleId: getUserRoleId(state),
  selectedDepartmentId: getSelectedDepartmentId(state),
  ClientId: getClientid(state),
  userList: getUserList(state),
  userInfo: getUserInfo(state),
});

export default connect(mapStateToProps, null)(LocationBasedPresentStaff);